import { create } from 'zustand';
import { persist, type PersistStorage } from 'zustand/middleware';
import type { SlaylistPreview } from '@/types/Wishlist';

type SlaylistOverride = {
  name: string;
  id: number;
};

interface SlaylistSelectorData {
  productId?: number;
  wishlist?: SlaylistPreview;
  productImage?: string;
  selectedSlaylist?: string;
}

interface SlaylistSelectorStore {
  isOpen: boolean;
  data: SlaylistSelectorData | null;
  productImage: string;
  selectedSlaylist: string;
  productId: number;
  wishlist: null;
  slaylists: SlaylistPreview[];
  productSlaylistOverrides: Record<number, SlaylistOverride | null>;
  openSlaylistSelector: (data: SlaylistSelectorData) => void;
  closeSlaylistSelector: () => void;
  setSelectedSlaylist: (slaylistId: string) => void;
  getSelectedSlaylist: () => string;
  setSlaylists: (slaylists: SlaylistPreview[]) => void;
  getSlaylists: () => SlaylistPreview[];
  removeSlaylist: (slaylistId: number) => void;
  setProductId: (productId: number) => void;
  getProductId: () => number;
  setProductImage: (productImage: string) => void;
  getProductImage: () => string;
  getSlaylistIdbyName: (slaylistName: string) => number | undefined;
  setProductSlaylist: (
    productId: number,
    slaylistName: string,
    slaylistId: number
  ) => void;
  getProductSlaylist: (productId: number) => SlaylistOverride | null;
  removeProductSlaylist: (productId: number) => void;
}

type PersistedState = Pick<
  SlaylistSelectorStore,
  'selectedSlaylist' | 'slaylists' | 'productSlaylistOverrides'
>;

// Alternative approach with custom serialization (if you prefer to keep Map):
// const mapSerializer = {
//   serialize: (map: Map<any, any>) => Array.from(map.entries()),
//   deserialize: (data: any) => new Map(data)
// };

const storageAdapter: PersistStorage<PersistedState> = {
  getItem: (name) => {
    const str = sessionStorage.getItem(name);
    if (!str) return null;

    try {
      return JSON.parse(str);
    } catch (error) {
      console.error('Failed to parse session storage:', error);
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      sessionStorage.setItem(name, JSON.stringify(value));
    } catch (error) {
      console.error('Failed to save to session storage:', error);
    }
  },
  removeItem: (name) => {
    try {
      sessionStorage.removeItem(name);
    } catch (error) {
      console.error('Failed to remove from session storage:', error);
    }
  },
};

const useSlaylistSelectorStore = create<SlaylistSelectorStore>()(
  persist(
    (set, get) => ({
      isOpen: false,
      data: null,
      selectedSlaylist: '',
      productId: 0,
      productImage: '',
      slaylists: [],
      wishlist: null,
      productSlaylistOverrides: {},
      openSlaylistSelector: (data) =>
        set({
          isOpen: true,
          data,
          productId: data.productId || 0,
          productImage: data.productImage || '',
        }),
      closeSlaylistSelector: () => set({ isOpen: false, data: null }),
      setSelectedSlaylist: (slaylistId) =>
        set({ selectedSlaylist: slaylistId }),
      getSelectedSlaylist: () => get().selectedSlaylist,
      setSlaylists: (slaylists) => set({ slaylists }),
      getSlaylists: () => get().slaylists,
      removeSlaylist: (slaylistId) =>
        set((state) => ({
          slaylists: state.slaylists.filter(
            (slaylist) => slaylist.id !== slaylistId
          ),
        })),
      setProductId: (productId) => set({ productId }),
      getProductId: () => get().productId,
      setProductImage: (productImage) => set({ productImage }),
      getProductImage: () => get().productImage,

      getSlaylistIdbyName: (slaylistName) => {
        const slaylists = get().slaylists;
        const slaylist = slaylists.find((s) => s.name === slaylistName);
        return slaylist?.id;
      },

      setProductSlaylist: (productId, slaylistName, slaylistId) =>
        set((state) => ({
          productSlaylistOverrides: {
            ...state.productSlaylistOverrides,
            [productId]: { name: slaylistName, id: slaylistId },
          },
        })),

      getProductSlaylist: (productId) => {
        return get().productSlaylistOverrides[productId] || null;
      },

      removeProductSlaylist: (productId) =>
        set((state) => {
          const newOverrides = { ...state.productSlaylistOverrides };
          delete newOverrides[productId];
          return { productSlaylistOverrides: newOverrides };
        }),
    }),
    {
      name: 'slaylist-selector-storage',
      storage: storageAdapter,
      partialize: (state): PersistedState => ({
        selectedSlaylist: state.selectedSlaylist,
        slaylists: state.slaylists,
        productSlaylistOverrides: state.productSlaylistOverrides,
      }),
      // Add version to handle migrations
      version: 1,
    }
  )
);

export default useSlaylistSelectorStore;

// Utility function to clear corrupted session storage
export const clearSlaylistSessionStorage = (): void => {
  try {
    sessionStorage.removeItem('slaylist-selector-storage');
  } catch (error) {
    console.error('Failed to clear session storage:', error);
  }
};

// Function to check and fix corrupted session storage
export const fixSlaylistSessionStorage = (): void => {
  try {
    const stored = sessionStorage.getItem('slaylist-selector-storage');
    if (stored) {
      const parsed = JSON.parse(stored);
      let state = parsed;
      let depth = 0;

      while (state && state.state && typeof state.state === 'object') {
        state = state.state;
        depth++;
        if (depth > 5) break; // Prevent infinite loops
      }

      if (depth > 1) {
        clearSlaylistSessionStorage();
      }
    }
  } catch (error) {
    console.error('Failed to check session storage:', error);
    clearSlaylistSessionStorage();
  }
};
